import { useEffect } from "react";

const SITE_NAME = "Interclub Poker Circuit";
const DEFAULT_DESC =
  "Find pub poker tournaments near you, RSVP to upcoming games, and track league leaderboards across the Interclub Poker Circuit.";

function setMeta(attr: "name" | "property", key: string, content: string) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export interface SeoOptions {
  /** Page title — gets " · Interclub Poker Circuit" appended unless `fullTitle` is true. */
  title?: string;
  /** Pass an already fully-formed title (no suffix). */
  fullTitle?: boolean;
  /** Meta description for this page. */
  description?: string;
  /** Optional canonical path or full URL. Defaults to the current path. */
  canonicalPath?: string;
  /** Set noindex when true (e.g. dynamic pages we don't want indexed). */
  noindex?: boolean;
}

/**
 * Set per-page SEO tags at runtime. Updates document.title, meta description,
 * canonical link, and Open Graph mirrors. Safe to call from any component.
 */
export function useSeo(opts: SeoOptions) {
  const {
    title,
    fullTitle = false,
    description = DEFAULT_DESC,
    canonicalPath,
    noindex = false,
  } = opts;

  useEffect(() => {
    const finalTitle = title
      ? fullTitle
        ? title
        : `${title} · ${SITE_NAME}`
      : `${SITE_NAME} — Pub Poker Tournaments, Leagues & Leaderboards`;
    document.title = finalTitle;

    setMeta("name", "description", description);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", description);

    const path =
      canonicalPath ??
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const url =
      path.startsWith("http")
        ? path
        : typeof window !== "undefined"
          ? `${window.location.origin}${path}`
          : path;
    setLink("canonical", url);
    setMeta("property", "og:url", url);

    setMeta(
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow",
    );
  }, [title, fullTitle, description, canonicalPath, noindex]);
}
